import { createAdminClient, deleteAdminClient, updateAdminClient } from "./api";
import { initialAdminClient } from "./constants";
import { getApiErrorMessage } from "./formatters";

export function useClientActions({
  adminClientForm,
  authHeaders,
  fetchDashboard,
  isEditingClient,
  selectedAdminClient,
  setAdminClientForm,
  setIsClientEditorOpen,
  setIsDeleteClientDialogOpen,
  setIsTmaPackageDialogOpen,
  setLoading,
  setSelectedClientId,
  setSelectedTicket,
  setTmaPackageForm,
  tmaPackageForm,
  toast,
  updateSelectedTicketId,
}) {
  const handleCreateClient = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const payload = {
        full_name: adminClientForm.full_name,
        email: adminClientForm.email,
        managed_developer_id: adminClientForm.managed_developer_id || null,
        tma_enabled: adminClientForm.tma_enabled,
        tma_visible_to_client: adminClientForm.tma_enabled && adminClientForm.tma_visible_to_client,
        tma_minutes_total: isEditingClient ? 0 : Number(adminClientForm.tma_minutes_total || 0),
        ...(isEditingClient
          ? {
              tma_new_packages: adminClientForm.tma_new_packages.map((item) => ({
                label: item.label,
                minutes: Number(item.minutes || 0),
              })),
            }
          : {
              tma_package_label: adminClientForm.tma_package_label || "TMA Juin 2026",
            }),
        ...(adminClientForm.password ? { password: adminClientForm.password } : {}),
      };
      const response = isEditingClient
        ? await updateAdminClient(adminClientForm.id, payload, authHeaders)
        : await createAdminClient(payload, authHeaders);
      setAdminClientForm(initialAdminClient);
      setIsClientEditorOpen(false);
      setSelectedClientId(response.data.id);
      await fetchDashboard();
      toast({
        title: isEditingClient ? "Client mis à jour" : "Accès client créé",
        description: isEditingClient
          ? "Les informations du client ont été mises à jour."
          : "Le client peut maintenant se connecter ou utiliser Mot de passe oublie plus tard.",
      });
    } catch (error) {
      toast({
        title: isEditingClient ? "Mise à jour impossible" : "Création du client impossible",
        description: getApiErrorMessage(error, "Verifiez les informations saisies."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setAdminClientForm({
      id: client.id,
      full_name: client.full_name,
      email: client.email,
      password: "",
      managed_developer_id: client.managed_developer_id || "",
      tma_enabled: Boolean(client.tma_enabled),
      tma_visible_to_client: Boolean(client.tma_visible_to_client),
      tma_package_label: "TMA Juin 2026",
      tma_minutes_total: client.tma_minutes_total ?? "",
      tma_new_package_label: "",
      tma_new_package_minutes: "",
      tma_new_packages: [],
    });
    setIsClientEditorOpen(true);
    setSelectedClientId(client.id);
  };

  const addTmaPackageLine = () => {
    const minutes = Number(adminClientForm.tma_new_package_minutes || 0);
    if (!adminClientForm.tma_new_package_label.trim() || minutes <= 0) {
      toast({
        title: "Forfait incomplet",
        description: "Renseigne un nom de forfait et un nombre de minutes superieur a 0.",
        variant: "destructive",
      });
      return;
    }

    setAdminClientForm({
      ...adminClientForm,
      tma_new_packages: [
        ...adminClientForm.tma_new_packages,
        {
          id: `${Date.now()}-${adminClientForm.tma_new_packages.length}`,
          label: adminClientForm.tma_new_package_label.trim(),
          minutes,
        },
      ],
      tma_new_package_label: "",
      tma_new_package_minutes: "",
    });
  };

  const removeTmaPackageLine = (packageId) => {
    setAdminClientForm({
      ...adminClientForm,
      tma_new_packages: adminClientForm.tma_new_packages.filter((item) => item.id !== packageId),
    });
  };

  const openTmaPackageDialog = () => {
    if (!selectedAdminClient) return;
    setTmaPackageForm({
      label: "TMA Juillet 2026",
      minutes: "",
    });
    setIsTmaPackageDialogOpen(true);
  };

  const handleAddTmaPackage = async () => {
    if (!selectedAdminClient) return;
    const minutes = Number(tmaPackageForm.minutes || 0);
    if (!tmaPackageForm.label.trim() || minutes <= 0) {
      toast({
        title: "Forfait incomplet",
        description: "Renseigne un nom de forfait et un nombre de minutes superieur a 0.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        full_name: adminClientForm.full_name || selectedAdminClient.full_name,
        email: adminClientForm.email || selectedAdminClient.email,
        managed_developer_id: adminClientForm.managed_developer_id || selectedAdminClient.managed_developer_id || null,
        tma_enabled: true,
        tma_visible_to_client: Boolean(adminClientForm.tma_visible_to_client || selectedAdminClient.tma_visible_to_client),
        tma_minutes_total: 0,
        tma_new_packages: [
          {
            label: tmaPackageForm.label.trim(),
            minutes,
          },
        ],
      };
      const response = await updateAdminClient(selectedAdminClient.id, payload, authHeaders);
      setIsTmaPackageDialogOpen(false);
      setAdminClientForm((current) => ({
        ...current,
        tma_enabled: true,
        tma_visible_to_client: Boolean(response.data.tma_visible_to_client),
        tma_minutes_total: response.data.tma_minutes_total ?? "",
      }));
      await fetchDashboard();
      toast({
        title: "Forfait ajoute",
        description: "Le nouveau forfait TMA est enregistre.",
      });
    } catch (error) {
      toast({
        title: "Ajout impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetClientForm = () => {
    setAdminClientForm(initialAdminClient);
    setIsClientEditorOpen(false);
    setSelectedClientId("");
  };

  const openCreateClientForm = () => {
    setAdminClientForm(initialAdminClient);
    setSelectedClientId("");
    setIsClientEditorOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!adminClientForm.id) return;

    setLoading(true);
    try {
      await deleteAdminClient(adminClientForm.id, authHeaders);
      setIsDeleteClientDialogOpen(false);
      resetClientForm();
      setSelectedTicket(null);
      updateSelectedTicketId("");
      await fetchDashboard();
      toast({
        title: "Client bloque",
        description: "Son accès est coupé, mais ses tickets et son historique restent conservés.",
      });
    } catch (error) {
      toast({
        title: "Suppression impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    addTmaPackageLine,
    handleAddTmaPackage,
    handleCreateClient,
    handleDeleteClient,
    handleEditClient,
    openCreateClientForm,
    openTmaPackageDialog,
    removeTmaPackageLine,
    resetClientForm,
  };
}
